/**
 * Application protocol spoken over the WebRTC DataChannel.
 *
 * Two frame kinds share the single ordered+reliable channel:
 *  - JSON control messages (sent as strings)
 *  - Binary chunk frames (sent as ArrayBuffers), each prefixed with its index
 *
 * Distinguishing them is trivial: `typeof event.data === 'string'` → control.
 */

/** Negotiated file description, exchanged before any bytes flow (Feature 5). */
export interface FileMetadata {
  name: string;
  size: number;
  mime: string;
  chunkSize: number;
  totalChunks: number;
}

export type ControlMessage =
  | ({ type: 'metadata' } & FileMetadata)
  // Receiver → sender: ready to receive (after preparing its sink/resume state).
  | { type: 'ready'; missingChunks?: number[] }
  // Receiver → sender: connection re-established, retransmit only these chunks.
  | { type: 'resume'; missingChunks: number[] }
  // Sender → receiver: all chunks sent; final integrity hash to verify against.
  | { type: 'complete'; sha256: string }
  // Either direction: abort the transfer.
  | { type: 'cancel'; reason?: string }
  | { type: 'error'; message: string };

export const CHUNK_HEADER_BYTES = 4; // uint32 chunk index

/** Frame a chunk: [uint32 index big-endian][payload bytes]. */
export function encodeChunk(index: number, payload: ArrayBuffer): ArrayBuffer {
  const out = new ArrayBuffer(CHUNK_HEADER_BYTES + payload.byteLength);
  const view = new DataView(out);
  view.setUint32(0, index, false);
  new Uint8Array(out, CHUNK_HEADER_BYTES).set(new Uint8Array(payload));
  return out;
}

export interface DecodedChunk {
  index: number;
  payload: Uint8Array;
}

/** Parse a binary chunk frame back into its index + payload view. */
export function decodeChunk(buffer: ArrayBuffer): DecodedChunk {
  if (buffer.byteLength < CHUNK_HEADER_BYTES) {
    throw new Error('Chunk frame too small');
  }
  const index = new DataView(buffer).getUint32(0, false);
  const payload = new Uint8Array(buffer, CHUNK_HEADER_BYTES);
  return { index, payload };
}

export function encodeControl(msg: ControlMessage): string {
  return JSON.stringify(msg);
}

/** Parse + validate a control message. Returns null on anything malformed. */
export function decodeControl(data: string): ControlMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const type = (parsed as { type?: unknown }).type;
  if (typeof type !== 'string') return null;
  switch (type) {
    case 'metadata':
    case 'ready':
    case 'resume':
    case 'complete':
    case 'cancel':
    case 'error':
      return parsed as ControlMessage;
    default:
      return null;
  }
}

/** Compute how many chunks a file of `size` needs at `chunkSize`. */
export function computeTotalChunks(size: number, chunkSize: number): number {
  if (size === 0) return 0;
  return Math.ceil(size / chunkSize);
}
